import { useState } from 'react';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
  Tooltip,
} from 'recharts';
import { seriesColumnList } from '../../constants';
import { tokens } from '../../designTokens';
import { formatCount } from '../../utils/format';
import type { StatusCount } from '../../interfaces/Dashboard';

interface StatusDonutChartProps {
  /** Per-status counts, keyed by the status `name` the API returns. */
  data: StatusCount[];
  height?: number;
}

type ChartEntry = StatusCount & { color: string };

/** Picks legible text for a solid-colour background, e.g. white on purple, black on yellow. */
const getContrastText = (hex: string) => {
  const value = hex.replace('#', '');
  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? tokens.colors.primaryText : tokens.colors.white;
};

interface ActiveShapeProps {
  cx?: number;
  cy?: number;
  innerRadius?: number;
  outerRadius?: number;
  startAngle?: number;
  endAngle?: number;
  fill?: string;
  cornerRadius?: number;
}

/** Grows the hovered slice a little, standing in for amCharts' hover pop. */
const renderActiveShape = (props: ActiveShapeProps) => {
  const {
    cx,
    cy,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    cornerRadius,
  } = props;
  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={Number(outerRadius) + 8}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
      cornerRadius={cornerRadius}
    />
  );
};

interface DonutTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: ChartEntry }>;
  total: number;
}

/** Tooltip background matches the hovered status's own colour, amCharts-style. */
const DonutTooltip = ({ active, payload, total }: DonutTooltipProps) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const entry = payload[0].payload;
  const rawCount = entry.count ?? 0;
  const count = Number.isFinite(rawCount) ? rawCount : 0;
  const percent = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';

  return (
    <div
      style={{
        backgroundColor: entry.color,
        color: getContrastText(entry.color),
        padding: '6px 12px',
        borderRadius: tokens.borderRadius.small,
        fontSize: 13,
        fontWeight: 600,
        boxShadow: tokens.shadows.medium,
      }}
    >
      {entry.name}: {percent}% ({formatCount(count)})
    </div>
  );
};

/**
 * Half-donut of request counts by status, with the grand total in the middle
 * and a legend list that stays in sync with the chart in both directions:
 * hovering a slice highlights its legend row, hovering a legend row grows and
 * highlights its slice (and dims the rest) — matching the amCharts behaviour
 * of the legacy dashboard.
 *
 * Replaces the amCharts 4 half-PieChart (startAngle 180 / endAngle 360). In
 * Recharts angles run counter-clockwise from 3 o'clock, so 180 -> 0 produces
 * the same upper half.
 */
const StatusDonutChart = ({ data, height = 480 }: StatusDonutChartProps) => {
  // Tracked by name rather than array index, because the Pie only ever
  // renders the non-zero slices (see pieData below) while the legend lists
  // every status - the two arrays don't share indices.
  const [activeName, setActiveName] = useState<string | null>(null);

  // Order slices by the canonical series order and drop statuses the API
  // omitted, so slice colours stay stable between health systems.
  const chartData: ChartEntry[] = seriesColumnList
    .map((column) => {
      const match = data.find((entry) => entry.name === column.field);
      return match ? { ...match, color: column.color } : null;
    })
    .filter((entry): entry is ChartEntry => entry !== null);

  // `Number.isFinite` here, not `?? 0`: nullish coalescing only replaces
  // null/undefined, so a NaN that has already leaked into `entry.count`
  // upstream would otherwise poison this sum (and every display below it).
  const total = chartData.reduce((sum, entry) => {
    const value = entry.count ?? 0;
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);

  // Recharts' paddingAngle carves out a gap between every adjacent pair in
  // the data array, zero-value entries included. With most statuses at 0 and
  // one carrying the whole total, that gap allowance still comes out of the
  // arc, so the one visible slice ends up offset to one side instead of
  // centred - the donut reads as crooked rather than a level dome. Feeding
  // the Pie only the non-zero slices removes those phantom gaps; the legend
  // still lists every status via chartData.
  const pieData = chartData.filter((entry) => (entry.count ?? 0) > 0);
  const activeIndex = activeName
    ? pieData.findIndex((entry) => entry.name === activeName)
    : -1;

  if (chartData.length === 0) {
    return (
      <div
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: tokens.colors.secondaryText,
        }}
      >
        No summary data available for this health system.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'stretch', gap: 16, height }}>
      <div style={{ flex: '1 1 55%', minWidth: 0 }}>
        <ResponsiveContainer width='100%' height='100%'>
          <PieChart margin={{ top: 16, right: 8, bottom: 16, left: 8 }}>
            <Pie
              data={pieData}
              dataKey='count'
              nameKey='name'
              cx='50%'
              cy='85%'
              startAngle={180}
              endAngle={0}
              innerRadius='55%'
              outerRadius='100%'
              paddingAngle={1.5}
              cornerRadius={6}
              isAnimationActive
              animationDuration={400}
              labelLine={false}
              activeIndex={activeIndex >= 0 ? activeIndex : undefined}
              activeShape={renderActiveShape}
              onMouseEnter={(_, index) =>
                setActiveName(pieData[index]?.name ?? null)
              }
              onMouseLeave={() => setActiveName(null)}
            >
              {pieData.map((entry, index) => (
                <Cell
                  key={entry.name}
                  fill={entry.color}
                  stroke={entry.color}
                  fillOpacity={
                    activeName === null || activeIndex === index ? 1 : 0.35
                  }
                />
              ))}
            </Pie>

            <text
              x='50%'
              y='78%'
              textAnchor='middle'
              dominantBaseline='middle'
              style={{ fill: tokens.colors.primaryText }}
            >
              <tspan fontSize={18}>Total:</tspan>
              <tspan x='50%' dy='1.4em' fontSize={30} fontWeight={700}>
                {formatCount(total)}
              </tspan>
            </text>

            <Tooltip content={<DonutTooltip total={total} />} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div
        style={{
          flex: '0 0 300px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '8px 4px',
        }}
      >
        {chartData.map((entry) => {
          const isActive = activeName === entry.name;
          return (
            <div
              key={entry.name}
              onMouseEnter={() => setActiveName(entry.name)}
              onMouseLeave={() => setActiveName(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '4px 10px',
                borderRadius: tokens.borderRadius.small,
                backgroundColor: isActive ? `${entry.color}22` : 'transparent',
                transition: 'background-color 0.15s ease',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 3,
                    backgroundColor: entry.color,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: isActive ? 700 : 400,
                    color: tokens.colors.primaryText,
                  }}
                >
                  {entry.name}
                </span>
              </span>
              <span
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: tokens.colors.primaryText,
                  minWidth: 48,
                  textAlign: 'right',
                }}
              >
                {formatCount(entry.count)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StatusDonutChart;
